// Presence chips for the viewer toolbar: who is on the doc and in which mode.
// Mode is signalled by a dot shape (edit = filled, view = ring) — not by color
// alone, for accessibility. Hover/aria gives the full email + mode.

import type { PresentUser } from "../presence";

interface Props {
  users: PresentUser[];
  meUid?: string;
}

// Deterministic chip color from the email, drawn from brand-adjacent tones.
const PALETTE = [
  "#75bfb8",
  "#023e60",
  "#596c33",
  "#b84239",
  "#2a2b52",
  "#c98a2b",
];

function colorFor(email: string): string {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function initials(name: string, email: string): string {
  const base = (name || email).trim();
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

export function PresenceBar({ users, meUid }: Props) {
  if (users.length === 0) return null;
  // Editors first, then alphabetical — keeps "who's editing" visually grouped.
  const sorted = [...users].sort((a, b) => {
    if (a.mode !== b.mode) return a.mode === "edit" ? -1 : 1;
    return a.email.localeCompare(b.email);
  });
  const editingCount = sorted.filter((u) => u.mode === "edit").length;

  return (
    <div
      className="presence-bar"
      role="group"
      aria-label={`${users.length} on this document, ${editingCount} editing`}
    >
      {sorted.map((u) => {
        const me = u.uid === meUid;
        const label = `${u.email}${me ? " (you)" : ""} · ${
          u.mode === "edit" ? "editing" : "viewing"
        }`;
        return (
          <span
            key={u.uid}
            className="presence-chip"
            style={{ background: colorFor(u.email) }}
            title={label}
            aria-label={label}
          >
            {initials(u.name, u.email)}
            <span
              className={`presence-dot presence-dot--${u.mode}`}
              aria-hidden="true"
            />
          </span>
        );
      })}
    </div>
  );
}
